import { useForm } from "@tanstack/react-form";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { useMemo } from "react";
import { artifactQueries } from "../../hooks/artifactQueries.ts";
import { isFormAnswered } from "../../types/artifacts.ts";
import { useSubmitForm } from "../../hooks/useArtifacts.tsx";
import { useIsSharedThread } from "../../hooks/useIsSharedThread.ts";
import { cn } from "../../lib/utils.ts";
import type {
  AskUserFormField,
  AskUserFormFieldMultiSelect,
  AskUserFormFieldSelectHandle,
  AskUserFormFieldSelectOption,
  AskUserFormSchema,
  GetArtifactResp,
} from "../../types/artifacts.ts";
import { decodeMultiSelectValue, encodeMultiSelectValue } from "../../types/artifacts.ts";
import { useReleaseBrowsedArtifact } from "./browsedArtifact.ts";
import { InlineMarkdown } from "../markdown/InlineMarkdown.tsx";
import { Button } from "../ui/button.tsx";
import { CheckboxCard } from "../ui/checkbox-card.tsx";
import { Input } from "../ui/input.tsx";
import { Label } from "../ui/label.tsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select.tsx";
import { SmartTextarea } from "../messages/Omnibox/SmartTextarea.tsx";

/** We want to render a button group for selects unless there are a lot of options. */
const SELECT_BUTTON_GROUP_MAX_ITEMS = 6;

const AskUserFormFieldLabel = ({
  idx,
  isOnlyQuestion = false,
  ...props
}: React.ComponentPropsWithoutRef<typeof Label> & {
  idx: number;
  isOnlyQuestion?: boolean;
}) => {
  return (
    <Label {...props} className="text-sm @md:text-base font-normal text-foreground">
      {!isOnlyQuestion && <span className="px-1">{idx + 1}.</span>}
      <InlineMarkdown>{props.children as string}</InlineMarkdown>
    </Label>
  );
};

const ButtonGroupSelect = ({
  field,
  fieldId,
  value,
  onChange,
  disabled = false,
  idx,
  onSelect,
  isOnlyQuestion = false,
}: {
  field: AskUserFormFieldSelectOption | AskUserFormFieldSelectHandle;
  fieldId: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  idx: number;
  onSelect?: (value: string) => void;
  isOnlyQuestion?: boolean;
}) => {
  return (
    <>
      <AskUserFormFieldLabel htmlFor={fieldId} idx={idx} isOnlyQuestion={isOnlyQuestion}>
        {field.question}
      </AskUserFormFieldLabel>
      <div className="grid grid-cols-1 @lg:grid-cols-2 gap-3">
        {field.options.map((option) => {
          const isSelected = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                onSelect?.(option.value);
              }}
              disabled={disabled}
              className={cn(
                "flex gap-3 items-start justify-start p-4 rounded-xl cursor-pointer transition-colors text-left",
                "bg-white border border-zinc-200",
                !disabled && "hover:bg-[#edebe7]",
                isSelected && "border-primary",
                disabled && "cursor-not-allowed opacity-60",
              )}
            >
              {/* Text content */}
              <span className="flex-1 text-sm font-medium leading-5 text-zinc-900">
                <InlineMarkdown>{option.label}</InlineMarkdown>
              </span>

              {/* Radio indicator */}
              <div
                className={cn(
                  "size-5 shrink-0 rounded-full border transition-colors",
                  isSelected
                    ? "bg-primary border-primary"
                    : disabled
                      ? "bg-zinc-100 border-zinc-200"
                      : "bg-white border-zinc-200",
                )}
              >
                {isSelected && (
                  <div className="flex items-center justify-center size-full">
                    <div className="size-2.5 rounded-full bg-white" />
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
};

const DropdownSelect = ({
  field,
  fieldId,
  value,
  onChange,
  disabled = false,
  idx,
  onSelect,
  isOnlyQuestion = false,
}: {
  field: AskUserFormFieldSelectOption | AskUserFormFieldSelectHandle;
  fieldId: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  idx: number;
  onSelect?: (value: string) => void;
  isOnlyQuestion?: boolean;
}) => {
  return (
    <>
      <AskUserFormFieldLabel htmlFor={fieldId} idx={idx} isOnlyQuestion={isOnlyQuestion}>
        {field.question}
      </AskUserFormFieldLabel>
      <Select
        value={value}
        onValueChange={(newValue) => {
          onChange(newValue);
          onSelect?.(newValue);
        }}
        disabled={disabled}
      >
        <SelectTrigger className="max-w-75 data-[placeholder]:text-muted-foreground">
          <SelectValue placeholder="Select an option..." />
        </SelectTrigger>
        <SelectContent>
          {field.options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              <InlineMarkdown>{option.label}</InlineMarkdown>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </>
  );
};

const AskUserSelectOptionFormField = ({
  isOnlyQuestion = false,
  ...props
}: {
  field: AskUserFormFieldSelectOption | AskUserFormFieldSelectHandle;
  fieldId: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  idx: number;
  onSelect?: (value: string) => void;
  isOnlyQuestion?: boolean;
}) => {
  return props.field.options.length > SELECT_BUTTON_GROUP_MAX_ITEMS ? (
    <DropdownSelect {...props} isOnlyQuestion={isOnlyQuestion} />
  ) : (
    <ButtonGroupSelect {...props} isOnlyQuestion={isOnlyQuestion} />
  );
};

/**
 * Multi-select counterpart to `ButtonGroupSelect`. Reuses `CheckboxCard` rather
 * than the sibling's hand-rolled button so the two option grids can't drift
 * apart visually — the card styling is already identical.
 *
 * Never auto-submits: the user is mid-answer until they say otherwise, so the
 * form's Continue button is the only way out.
 */
const ButtonGroupMultiSelect = ({
  field,
  fieldId,
  value,
  onChange,
  disabled = false,
  idx,
  isOnlyQuestion = false,
}: {
  field: AskUserFormFieldMultiSelect;
  fieldId: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  idx: number;
  isOnlyQuestion?: boolean;
}) => {
  const selected = useMemo(() => decodeMultiSelectValue(value), [value]);

  const toggle = (optionValue: string) => {
    const next = selected.includes(optionValue)
      ? selected.filter((v) => v !== optionValue)
      : [...selected, optionValue];
    onChange(encodeMultiSelectValue(next));
  };

  return (
    <>
      <AskUserFormFieldLabel htmlFor={fieldId} idx={idx} isOnlyQuestion={isOnlyQuestion}>
        {field.question}
      </AskUserFormFieldLabel>
      <div className="grid grid-cols-1 @lg:grid-cols-2 gap-3">
        {field.options.map((option) => (
          <CheckboxCard
            key={option.value}
            label={option.label}
            showIcon={false}
            checked={selected.includes(option.value)}
            disabled={disabled}
            onCheckedChange={() => toggle(option.value)}
          />
        ))}
      </div>
    </>
  );
};

const AskUserTextFormField = ({
  field,
  fieldId,
  value,
  onChange,
  onBlur,
  disabled = false,
  idx,
  isOnlyQuestion = false,
}: {
  field: AskUserFormField;
  fieldId: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  disabled?: boolean;
  idx: number;
  isOnlyQuestion?: boolean;
}) => {
  const placeholderText = useMemo(() => {
    if (field.type === "newContact") {
      return field.handleType === "emailAddress" ? "Email address" : "Phone number";
    }
    if (field.type === "newHandle") {
      return field.handleType === "emailAddress" ? "Email address" : "Phone number";
    }
    if (field.type === "text" && field.inputType === "number") {
      return "Enter a number...";
    }
    if (field.type === "text" && field.inputType === "date") {
      return "Select a date...";
    }
    return "Enter your answer...";
  }, [field]);
  // A free-text answer is written in the composer's own box (BIG-9261), the same
  // one the omnibox card uses: @mentions with contact autocomplete, and growth
  // to ten lines before it scrolls. Numbers, dates and handles stay one-line
  // inputs: they are short by nature and rely on the input's own type for entry.
  const isFreeText = field.type === "text" && (field.inputType ?? "text") === "text";
  return (
    <>
      <AskUserFormFieldLabel htmlFor={fieldId} idx={idx} isOnlyQuestion={isOnlyQuestion}>
        {field.question}
      </AskUserFormFieldLabel>

      {isFreeText ? (
        // As in the omnibox card, the vertical inset lives on the pill, never on
        // the field: the mention overlay is pinned to the field's box with no
        // padding of its own, so a padded field draws its text below it.
        <div className="flex min-h-10 items-end rounded-[1.25rem] border border-input bg-popover px-3 py-[9px]">
          <SmartTextarea
            id={fieldId}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={placeholderText}
            aria-label="Your answer"
            maxRows={10}
            // A value present at mount is a stored or staged answer, not a
            // prefill for the box to expand into the scheduling flow.
            initialValueIsTyped
            // `block` drops the inline line-box gap under the field.
            className="block"
            disabled={disabled}
          />
        </div>
      ) : (
        <Input
          id={fieldId}
          type={field.type === "text" ? (field.inputType ?? "text") : "text"}
          value={value}
          onBlur={onBlur}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholderText}
          className="w-full max-w-75 bg-popover text-popover-foreground"
          disabled={disabled}
        />
      )}
    </>
  );
};

/**
 * Interactive form component for collecting user responses to questions.
 * Features button-style select for ≤5 options, dropdown for >5 options,
 * and responsive grid layout.
 */
export const AskUserForm = ({
  formSchema,
  artifactId,
  threadId,
  messageId,
  onSubmitted,
}: {
  formSchema: AskUserFormSchema;
  artifactId: string;
  threadId: number;
  messageId: number;
  /** Fired once the answers have landed. Each surface does its own thing with
   *  it: the thread collapses the artifact group, the canvas steps the review
   *  on to the next card (or closes). */
  onSubmitted?: () => void;
}) => {
  const submitFormMutation = useSubmitForm({ threadId, messageId });
  const queryClient = useQueryClient();
  const releaseBrowsedArtifact = useReleaseBrowsedArtifact();
  const isSharedThread = useIsSharedThread();

  const isSingleSelect = useMemo(() => {
    if (formSchema.fields.length !== 1) return false;
    return formSchema.fields[0].type === "select" || formSchema.fields[0].type === "selectHandle";
  }, [formSchema.fields]);
  // Check if form has already been submitted
  const isFormSubmitted = isFormAnswered(formSchema);
  // An unanswered question stays answerable for as long as it goes unanswered —
  // a form the conversation has scrolled past is still a question nobody replied
  // to, and the submit endpoint has never cared how recent the message was. Only
  // an answer already on record, or a thread the viewer doesn't own, closes it.
  const isFormDisabled = isFormSubmitted || isSharedThread;

  // Create default values for all fields, using stored values if they exist
  const defaultValues = formSchema.fields.reduce(
    (acc, field) => {
      acc[field.id] = formSchema.values?.[field.id] || "";
      return acc;
    },
    {} as Record<string, string>,
  );

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      if (isFormSubmitted) return; // Don't submit if already submitted

      await submitFormMutation.mutateAsync({
        artifactId,
        values: value,
      });

      /**
       * Update the artifact cache with the submitted values
       * Otherwise once the form is collapsed by the ArtifactGroup component, the form will reset to the default values.
       */
      const artifactQueryKey = artifactQueries.detail(threadId, messageId, artifactId).queryKey;
      queryClient.setQueryData(artifactQueryKey, (oldData: GetArtifactResp | undefined) => {
        if (oldData?.artifactType !== "userResponse" || !oldData.form) {
          return oldData;
        }
        return {
          ...oldData,
          form: {
            ...oldData.form,
            values: value,
          },
        };
      });

      // This is every expanded surface a form is answered on — the canvas card
      // and both lightboxes — and none of them is the omnibox card that may be
      // showing the same form below. That card holds the snapshot it was opened
      // with, so without this it goes on asking questions the user has just
      // answered (BIG-7551).
      releaseBrowsedArtifact(artifactId);

      onSubmitted?.();
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
      className={cn("flex flex-col my-3 gap-6", isFormSubmitted && "text-muted-foreground")}
    >
      {formSchema.fields.map((field, idx) => (
        <form.Field key={field.id} name={field.id}>
          {(formField) => (
            <div className="flex flex-col gap-3">
              {field.type === "multiSelect" && (
                <ButtonGroupMultiSelect
                  field={field}
                  fieldId={field.id}
                  value={formField.state.value}
                  onChange={formField.handleChange}
                  disabled={isFormDisabled}
                  idx={idx}
                  isOnlyQuestion={formSchema.fields.length === 1}
                />
              )}
              {(field.type === "select" || field.type === "selectHandle") && (
                <AskUserSelectOptionFormField
                  field={field}
                  fieldId={field.id}
                  value={formField.state.value}
                  onChange={formField.handleChange}
                  disabled={isFormDisabled}
                  idx={idx}
                  isOnlyQuestion={formSchema.fields.length === 1}
                  onSelect={isSingleSelect ? () => form.handleSubmit() : undefined}
                />
              )}
              {["text", "newContact", "newHandle"].includes(field.type) && (
                <AskUserTextFormField
                  field={field}
                  fieldId={field.id}
                  value={formField.state.value}
                  onChange={formField.handleChange}
                  onBlur={formField.handleBlur}
                  disabled={isFormDisabled}
                  idx={idx}
                  isOnlyQuestion={formSchema.fields.length === 1}
                />
              )}
            </div>
          )}
        </form.Field>
      ))}

      {!isFormSubmitted && !isSingleSelect && (
        <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
          {([canSubmit, isFormSubmitting]) => (
            <div className="">
              <Button
                type="submit"
                disabled={!canSubmit || isFormSubmitting || isFormDisabled}
                variant="default"
                size="sm"
                data-statsig-name="artifact-ask-user-submit"
              >
                {isFormSubmitted ? (
                  "Submitted"
                ) : isFormSubmitting ? (
                  "Submitting..."
                ) : (
                  <>
                    Continue
                    <ArrowRight className="size-4" />
                  </>
                )}
              </Button>
            </div>
          )}
        </form.Subscribe>
      )}
    </form>
  );
};
